'use client';

import {Button} from '@udemy-v2/react-core-components';
import {useI18n} from '@udemy-v2/i18n';
import {publishEvent} from '@udemy-v2/event-tracking';

import {
    COMMUNITY_UI_REGION,
    buildJoinSelectedEvent,
} from '@/components/tracking/instructor-community-events';

import styles from './community-header.module.scss';

interface CommunityHeaderProps {
    /**
     * Community display name — rendered as the section title. Presentational
     * only; the public profile page (PUBLISHED) and the Phase-2 instructor
     * preview tab (DRAFT) both pass already-resolved data here.
     */
    name?: string | null;
    /** One-line core promise — rendered as the subtitle. */
    corePromise?: string | null;
    /** Community URL for the CTA. */
    communityUrl?: string | null;
    /**
     * Click handler for the CTA. Optional; defaults to a no-op when
     * communityUrl is not provided.
     */
    onJoinClick?: () => void;
    /**
     * Whether the viewer is already subscribed to the community. When true the
     * CTA reads "Open Community" (and the caller passes the base community URL)
     * instead of the "Join my community" promo for non-members.
     */
    isSubscribed?: boolean;
    /**
     * Profile owner's user id, for the `InstructorCommunityJoinSelected` event.
     * Supplied on the public profile path only.
     */
    instructorId?: string | null;
    /**
     * Community id, for the same event. Both ids must be present for it to
     * fire — the instructor DRAFT preview passes neither, so previewing your
     * own profile publishes nothing.
     */
    communityId?: string | null;
}

function buildJoinUrl(communityUrl: string): string {
    const url = new URL(communityUrl);
    url.searchParams.set('automatic_login', 'true');
    return url.toString();
}

const noop = () => {};

/**
 * Header division of the community section (Figma node 2944:40169): centred
 * community title, core-promise subtitle, and a primary CTA. Non-members see
 * "Join my community"; already-subscribed viewers see "Open Community". When
 * communityUrl is provided, clicking the button navigates to the community with
 * automatic_login=true search param appended.
 *
 * Selecting the non-subscribed CTA publishes `InstructorCommunityJoinSelected`
 * with `uiRegion: instructor_profile.community_section.header`.
 */
export function CommunityHeader({
    name,
    corePromise,
    communityUrl,
    onJoinClick,
    isSubscribed = false,
    instructorId,
    communityId,
}: CommunityHeaderProps) {
    const {gettext} = useI18n();

    const navigate = onJoinClick ?? (communityUrl ? () => {
        const joinUrl = buildJoinUrl(communityUrl);
        window.location.href = joinUrl;
    } : noop);

    // Only the non-subscribed CTA is a join; subscribed viewers get
    // "Open Community", which is not part of this event.
    const handleJoinClick = () => {
        if (!isSubscribed) {
            const event = buildJoinSelectedEvent({
                instructorId,
                communityId,
                uiRegion: COMMUNITY_UI_REGION.sectionHeader,
            });
            if (event) {
                publishEvent(event);
            }
        }
        navigate();
    };

    return (
        <div className={styles['community-header']}>
            <div className={styles['text-block']}>
                {name ? <h1 className={styles['title']}>{name}</h1> : null}
                {corePromise ? <p className={styles['promise']}>{corePromise}</p> : null}
            </div>
            <Button
                udStyle="primary"
                size="large"
                className={styles['join-button']}
                onClick={handleJoinClick}
                data-purpose="join-community"
            >
                {isSubscribed ? gettext('Open Community') : gettext('Join my community')}
            </Button>
        </div>
    );
}
